import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Emoji } from '@/components/Emoji';
import { GlassButton } from '@/components/GlassButton';
import { Icon, type IconName } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { SpotlightCard } from '@/components/SpotlightCard';
import { COURSES, VOCAB_BOOKS, VOCAB_BY_ID } from '@/lib/data';
import { useAppStore } from '@/lib/store';
import { isDue } from '@/lib/srs';

export function DashboardPage(): JSX.Element {
  const user = useAppStore((s) => s.user);
  const progress = useAppStore((s) => s.progress);
  const mistakes = useAppStore((s) => s.mistakes);
  const srs = useAppStore((s) => s.srs);
  const customs = useAppStore((s) => s.customCourses);
  const combo = useAppStore((s) => s.combo);
  const aiUsed = useAppStore((s) => s.aiQuotaUsedToday);
  const aiCap = user.plan === 'pro' ? 300 : 30;

  const completed = new Set(progress.completedSentenceIds);
  const allLessons = COURSES.flatMap((c) => c.lessons);
  const recommendedLesson =
    allLessons.find((l) => l.sentences.some((s) => !completed.has(s.id))) ?? allLessons[0]!;
  const recommendedCourse = COURSES.find((c) => c.id === recommendedLesson.courseId)!;

  const dueCount = Object.values(srs).filter((c) => isDue(c)).length;
  const unreviewedMistakes = mistakes.filter((m) => !m.reviewed).length;

  const hour = new Date().getHours();
  const greeting = hour < 6 ? '夜深了' : hour < 12 ? '早上好' : hour < 18 ? '下午好' : '晚上好';

  return (
    <div className="space-y-12">
      {/* Hero greeting */}
      <Reveal>
        <div>
          <div className="text-xs text-cream-400 font-arcade tracking-widest">{greeting.toUpperCase()},</div>
          <h1 className="mt-1 font-display text-3xl md:text-5xl font-extrabold text-white">
            {user.name} <span className="text-cream-400">·</span>{' '}
            <span className="text-gradient-warm">今天开局</span>
          </h1>
          <p className="mt-3 text-cream-300/90">
            连续 <span className="text-ember-300 font-bold">{progress.streakDays}</span> 天 ·
            累计 <span className="text-honey-300 font-bold">{progress.totalMinutes}</span> 分钟 ·
            <span className="text-mint-300 font-bold"> {progress.xp.toLocaleString()}</span> XP
            {combo.allTimeBest > 0 && (
              <span className="ml-2">· 最高连击 <span className="text-honey-300 font-bold font-score">×{combo.allTimeBest}</span></span>
            )}
          </p>
        </div>
      </Reveal>

      {/* AI quota strip — flashes red when ≥80% to nudge upgrade */}
      <Reveal delay={0.03}>
        {(() => {
          const pct = (aiUsed / aiCap) * 100;
          const hot = pct >= 80;
          return (
            <div className={`rounded-2xl border px-5 py-3 flex items-center gap-4 flex-wrap transition-colors ${
              hot
                ? 'border-ember-400/40 bg-ember-400/[0.06]'
                : 'border-honey-400/20 bg-honey-400/[0.03]'
            }`}>
              <Icon name={hot ? 'warn' : 'ai'} size="lg" className={hot ? 'text-ember-300' : 'text-honey-300'} />
              <div className="flex-1 min-w-[180px]">
                <div className="text-xs text-cream-400">
                  今日 AI ·{' '}
                  <span className={`font-mono font-bold ${hot ? 'text-ember-300' : 'text-honey-300'}`}>
                    {aiUsed} / {aiCap}
                  </span>
                  <span className="ml-2 text-cream-500">
                    {user.plan === 'pro' ? 'Pro 引擎' : '基础引擎'}
                    {hot && ' · 即将用尽'}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                  <motion.div
                    className={`h-full ${
                      hot
                        ? 'bg-gradient-to-r from-ember-400 to-ember-500'
                        : 'bg-gradient-to-r from-honey-400 to-mint-400'
                    }`}
                    animate={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
              </div>
              {user.plan === 'free' && (
                <Link
                  to="/app/plan"
                  className={`text-xs font-arcade tracking-widest ${
                    hot
                      ? 'text-ember-300 hover:text-ember-200 animate-pulse-soft'
                      : 'text-honey-300 hover:text-honey-200'
                  }`}
                >
                  UPGRADE →
                </Link>
              )}
            </div>
          );
        })()}
      </Reveal>

      {/* Today's recommended lesson — hero card */}
      <Reveal delay={0.05}>
        <SpotlightCard spotlightColor="rgba(0, 240, 255, 0.32)" className="overflow-hidden">
          <div className="flex flex-col md:flex-row gap-6 md:items-center">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] text-honey-300 font-arcade">
                TODAY'S MISSION · 推荐课程
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold text-white flex items-center gap-3 flex-wrap">
                <Emoji char={recommendedCourse.emoji} size={32} />
                {recommendedLesson.title}
              </h2>
              <p className="mt-2 text-sm text-cream-300/90">
                来自《{recommendedCourse.title}》 ·{' '}
                {recommendedLesson.sentences.length} 个句子 · 约 {recommendedLesson.sentences.length * 2} 分钟
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <GlassButton to={`/app/study/puzzle/${recommendedLesson.id}`} size="md" variant="solid">
                  🧩 PUZZLE
                </GlassButton>
                <GlassButton to={`/app/study/typing/${recommendedLesson.id}`} size="md">
                  ⌨️ TYPING
                </GlassButton>
                <GlassButton to={`/app/study/speaking/${recommendedLesson.id}`} size="md">
                  🎙️ SPEAKING
                </GlassButton>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="rounded-3xl bg-gradient-to-br from-honey-400/20 to-mint-400/20 p-6 w-48 grid place-items-center scanlines relative">
                <Emoji char={recommendedCourse.emoji} size={96} />
              </div>
            </div>
          </div>
        </SpotlightCard>
      </Reveal>

      {/* Quick actions row */}
      <div className="grid gap-5 md:grid-cols-4">
        <Reveal delay={0.08}>
          <QuickCard
            to="/app/vocab/review"
            icon="vocab"
            title="单词复习"
            count={dueCount}
            unit="张待复习"
            tone="lime"
            cta={dueCount === 0 ? '都复习完了' : '开始复习 →'}
            disabled={dueCount === 0}
          />
        </Reveal>
        <Reveal delay={0.12}>
          <QuickCard
            to="/app/mistakes"
            icon="mistakes"
            title="错题回顾"
            count={unreviewedMistakes}
            unit="个待回顾"
            tone="pink"
            cta={unreviewedMistakes === 0 ? '当前没有错题' : '回顾错题 →'}
            disabled={unreviewedMistakes === 0}
          />
        </Reveal>
        <Reveal delay={0.16}>
          <QuickCard
            to="/app/courses"
            icon="courses"
            title="课程市场"
            count={COURSES.length + customs.length}
            unit="门可选"
            tone="cyan"
            cta="浏览课程 →"
          />
        </Reveal>
        <Reveal delay={0.2}>
          <QuickCard
            to="/app/create"
            icon="create"
            title="自己造课"
            count={customs.length}
            unit={customs.length === 0 ? '5 秒造一节' : '已创建'}
            tone="cream"
            cta={customs.length === 0 ? '开始创建 →' : '继续创建 →'}
          />
        </Reveal>
      </div>

      {/* Recent custom courses */}
      {customs.length > 0 && (
        <Reveal>
          <div>
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold text-white">我的私人课</h2>
                <p className="text-sm text-cream-400 mt-1">你亲手造的课</p>
              </div>
              <Link to="/app/create" className="text-sm text-honey-300 hover:text-honey-200">
                + 再造一节 →
              </Link>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {customs.slice(0, 6).map((c) => (
                <Link
                  key={c.id}
                  to={`/app/courses/${c.id}`}
                  className="group rounded-3xl border border-honey-400/15 bg-ink-900/40 p-5 transition-all hover:border-honey-400/40 hover:-translate-y-1 flex items-center gap-4"
                >
                  <Emoji char={c.emoji} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-white truncate">{c.title}</div>
                    <div className="text-xs text-cream-400 mt-1">{c.sentences.length} 句 · {new Date(c.createdAt).toLocaleDateString('zh-CN')}</div>
                  </div>
                  <span className="text-honey-400 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Recent courses */}
      <Reveal>
        <div>
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">继续你的课程</h2>
              <p className="text-sm text-cream-400 mt-1">从上次的地方继续</p>
            </div>
            <Link to="/app/courses" className="text-sm text-honey-300 hover:text-honey-200">
              全部 →
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {COURSES.slice(0, 4).map((c) => {
              const totalSentences = c.lessons.reduce((acc, l) => acc + l.sentences.length, 0);
              const done = c.lessons.reduce(
                (acc, l) => acc + l.sentences.filter((s) => completed.has(s.id)).length,
                0,
              );
              const pct = totalSentences === 0 ? 0 : Math.round((done / totalSentences) * 100);
              return (
                <Link
                  key={c.id}
                  to={`/app/courses/${c.id}`}
                  className="group rounded-3xl border border-honey-400/15 bg-ink-900/40 p-5 transition-all hover:border-honey-400/40 hover:-translate-y-1"
                >
                  <div className="mb-3"><Emoji char={c.emoji} size={36} /></div>
                  <div className="font-display font-bold text-white">{c.title}</div>
                  <div className="mt-1 text-xs text-cream-400">{c.lessons.length} 节课 · 难度 {'⭐'.repeat(c.difficulty)}</div>
                  <div className="mt-4 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-honey-400 via-mint-400 to-ember-400"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-cream-400">{pct}% 完成</div>
                </Link>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* Vocab books */}
      <Reveal>
        <div>
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="font-display text-2xl font-bold text-white">我的单词本</h2>
              <p className="text-sm text-cream-400 mt-1">按场景挑一本开始</p>
            </div>
            <Link to="/app/vocab" className="text-sm text-honey-300 hover:text-honey-200">
              全部 →
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {VOCAB_BOOKS.map((b) => {
              const due = b.wordIds.filter((id) => {
                const card = srs[id];
                return card === undefined || isDue(card);
              }).length;
              return (
                <Link
                  key={b.id}
                  to="/app/vocab"
                  className="group rounded-3xl border border-honey-400/15 bg-ink-900/40 p-5 transition-all hover:border-honey-400/40 hover:-translate-y-1 flex gap-4"
                >
                  <Emoji char={b.emoji} size={44} />
                  <div className="flex-1">
                    <div className="font-display font-bold text-white">{b.title}</div>
                    <div className="text-xs text-cream-400 mt-1">{b.wordIds.length} 词 · {due} 待复习</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {b.wordIds.slice(0, 4).map((id) => {
                        const w = VOCAB_BY_ID[id];
                        if (w === undefined) return null;
                        return (
                          <span key={id} className="text-xs px-1.5 py-0.5 rounded bg-white/[0.04] text-cream-300 font-mono">
                            {w.en}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function QuickCard({
  to,
  icon,
  title,
  count,
  unit,
  tone,
  cta,
  disabled = false,
}: {
  to: string;
  icon: IconName;
  title: string;
  count: number;
  unit: string;
  tone: 'lime' | 'pink' | 'cyan' | 'cream';
  cta: string;
  disabled?: boolean;
}): JSX.Element {
  const toneClass =
    tone === 'lime' ? 'text-mint-300' :
    tone === 'pink' ? 'text-ember-300' :
    tone === 'cyan' ? 'text-honey-300' :
    'text-cream-200';
  return (
    <Link
      to={disabled ? '#' : to}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      className={`block rounded-3xl border border-honey-400/15 bg-ink-900/40 p-5 transition-all ${
        disabled ? 'opacity-60 cursor-default' : 'hover:border-honey-400/40 hover:-translate-y-1'
      }`}
    >
      <div className="flex items-start justify-between">
        <Icon name={icon} size="lg" className={toneClass} />
        <span className={`font-score text-4xl ${toneClass} text-neon`}>{count}</span>
      </div>
      <div className="mt-4 font-display font-bold text-white">{title}</div>
      <div className="text-xs text-cream-400 mt-0.5">{unit}</div>
      <div className={`mt-4 text-xs font-arcade tracking-widest ${disabled ? 'text-cream-400' : toneClass}`}>{cta}</div>
    </Link>
  );
}
